import { useMemo, type CSSProperties } from "react";
import type {
  DrawdownPoint,
  HistoricalBalancePoint,
  LiveBalancePoint,
  LiveEquityPoint,
  MonthlyPerformance,
  PublicEquity,
} from "../../contracts/src/index.ts";

export function formatMoney(value: number | null | undefined, currency: string): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "N/A";
  try {
    return new Intl.NumberFormat("zh-TW", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency} ${value.toLocaleString("zh-TW", { maximumFractionDigits: 2 })}`;
  }
}

export function formatPercent(value: number | null | undefined, signed = false): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "N/A";
  const prefix = signed && value > 0 ? "+" : "";
  return `${prefix}${value.toLocaleString("zh-TW", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
}

export function formatDate(timestamp: number | null | undefined, timeZone = "UTC"): string {
  if (!timestamp) return "N/A";
  return new Intl.DateTimeFormat("zh-TW", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(timestamp * 1000);
}

function downsample<T extends { recordedAt: number }>(points: T[], target: number): T[] {
  if (points.length <= target) return points;
  const step = Math.ceil(points.length / target);
  const sampled = points.filter((_, index) => index % step === 0);
  const last = points.at(-1);
  if (last && sampled.at(-1) !== last) sampled.push(last);
  return sampled;
}

function linePath(
  points: { recordedAt: number; value: number }[],
  width: number,
  height: number,
  min: number,
  max: number,
  from: number,
  to: number,
): string {
  const spread = max - min || 1;
  const timeSpread = to - from || 1;
  return points.map((point, index) => {
    const x = ((point.recordedAt - from) / timeSpread) * width;
    const y = height - ((point.value - min) / spread) * height;
    return `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");
}

function PointTitles({
  points,
  valueOf,
  currency,
  min,
  max,
  from,
  to,
  width,
  height,
  label,
}: {
  points: { recordedAt: number }[];
  valueOf: (point: { recordedAt: number }) => number;
  currency: string;
  min: number;
  max: number;
  from: number;
  to: number;
  width: number;
  height: number;
  label: string;
}) {
  const step = Math.max(1, Math.ceil(points.length / 80));
  return points.filter((_, index) => index % step === 0 || index === points.length - 1).map((point) => {
    const value = valueOf(point);
    const x = ((point.recordedAt - from) / (to - from || 1)) * width;
    const y = height - ((value - min) / (max - min || 1)) * height;
    return (
      <circle key={`${label}-${point.recordedAt}`} cx={x} cy={y} r="8" className="chart-hit">
        <title>{`${label} · ${formatDate(point.recordedAt)} · ${formatMoney(value, currency)}`}</title>
      </circle>
    );
  });
}

export function EquityChart({ equity, currency }: { equity: PublicEquity | null; currency: string }) {
  const historical = useMemo<HistoricalBalancePoint[]>(
    () => downsample(equity?.historical_balance.points ?? [], 500),
    [equity],
  );
  const liveBalance = useMemo<LiveBalancePoint[]>(
    () => downsample(equity?.live_balance.points ?? [], 900),
    [equity],
  );
  const liveEquity = useMemo<LiveEquityPoint[]>(
    () => downsample(equity?.live_equity.points ?? [], 900),
    [equity],
  );
  const total = historical.length + liveBalance.length + liveEquity.length;
  if (!total) {
    return (
      <div className="chart-empty" role="status">
        <span className="chart-empty-mark" aria-hidden="true" />
        <strong>尚無可繪製的績效資料</strong>
        <span>Snapshot 或 deals 完成上傳後，這裡會顯示真實資料；系統不會產生示意績效。</span>
      </div>
    );
  }

  const values = [
    ...historical.map((point) => point.balance),
    ...liveBalance.map((point) => point.balance),
    ...liveEquity.map((point) => point.equity),
  ];
  const timestamps = [
    ...historical.map((point) => point.recordedAt),
    ...liveBalance.map((point) => point.recordedAt),
    ...liveEquity.map((point) => point.recordedAt),
  ];
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const padding = Math.max((rawMax - rawMin) * 0.12, Math.abs(rawMax) * 0.01, 1);
  const min = rawMin - padding;
  const max = rawMax + padding;
  const from = Math.min(...timestamps);
  const to = Math.max(...timestamps);
  const width = 1200;
  const height = 360;
  const historicalPath = linePath(
    historical.map((point) => ({ recordedAt: point.recordedAt, value: point.balance })),
    width, height, min, max, from, to,
  );
  const liveBalancePath = linePath(
    liveBalance.map((point) => ({ recordedAt: point.recordedAt, value: point.balance })),
    width, height, min, max, from, to,
  );
  const liveEquityPath = linePath(
    liveEquity.map((point) => ({ recordedAt: point.recordedAt, value: point.equity })),
    width, height, min, max, from, to,
  );
  const firstLiveX = liveEquity.length
    ? ((liveEquity[0].recordedAt - from) / (to - from || 1)) * width
    : 0;
  const lastLiveX = liveEquity.length
    ? ((liveEquity.at(-1)!.recordedAt - from) / (to - from || 1)) * width
    : 0;

  return (
    <div className="chart-wrap">
      <div className="chart-scale" aria-hidden="true">
        <span>{formatMoney(max, currency)}</span>
        <span>{formatMoney((max + min) / 2, currency)}</span>
        <span>{formatMoney(min, currency)}</span>
      </div>
      <svg className="equity-chart" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img"
        aria-label={`績效曲線：Historical Balance ${historical.length} 點、Live Balance ${liveBalance.length} 點、Live Equity ${liveEquity.length} 點`}>
        <defs>
          <linearGradient id="live-equity-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3ee7bd" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#3ee7bd" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, .25, .5, .75, 1].map((ratio) => (
          <line key={ratio} x1="0" x2={width} y1={height * ratio} y2={height * ratio} className="chart-grid" />
        ))}
        {liveEquity.length > 1 && (
          <path d={`${liveEquityPath} L${lastLiveX},${height} L${firstLiveX},${height} Z`} fill="url(#live-equity-area)" />
        )}
        {historicalPath && <path d={historicalPath} className="historical-balance-line" />}
        {liveBalancePath && <path d={liveBalancePath} className="live-balance-line" />}
        {liveEquityPath && <path d={liveEquityPath} className="live-equity-line" />}
        <PointTitles points={historical} valueOf={(point) => (point as HistoricalBalancePoint).balance}
          currency={currency} min={min} max={max} from={from} to={to} width={width} height={height} label="Historical Balance" />
        <PointTitles points={liveBalance} valueOf={(point) => (point as LiveBalancePoint).balance}
          currency={currency} min={min} max={max} from={from} to={to} width={width} height={height} label="Live Balance" />
        <PointTitles points={liveEquity} valueOf={(point) => (point as LiveEquityPoint).equity}
          currency={currency} min={min} max={max} from={from} to={to} width={width} height={height} label="Live Equity" />
      </svg>
      <div className="chart-dates"><span>{formatDate(from)}</span><span>{formatDate(to)}</span></div>
    </div>
  );
}

export function MonthlyBarChart({ monthly }: { monthly: MonthlyPerformance[] }) {
  const points = monthly.slice(-18);
  const maximum = Math.max(1, ...points.flatMap((month) => month.returnPct === null ? [] : [Math.abs(month.returnPct)]));
  if (!points.length) return <div className="mini-empty">月績效資料不足</div>;
  return (
    <div className="monthly-bars" role="img" aria-label="最近 18 個月報酬長條圖">
      {points.map((month) => {
        const value = month.returnPct;
        const height = value === null ? 0 : Math.max(3, Math.abs(value) / maximum * 45);
        const detail = [month.month, formatPercent(value, true), month.status, month.source, month.reason]
          .filter(Boolean)
          .join(" · ");
        return (
          <div className={`month-bar-column${month.status === "MTD" ? " mtd" : ""}${month.status === "PARTIAL_INCEPTION" ? " partial" : ""}`} key={month.month} title={detail} tabIndex={0} aria-label={detail}>
            <span className="month-bar-value">{value === null ? "N/A" : formatPercent(value, true)}</span>
            <div className="month-bar-track">
              {value !== null && <i className={value >= 0 ? "positive" : "negative"} style={{ height: `${height}%` }} />}
            </div>
            <span>{month.month.slice(2).replace("-", "/")}</span>
          </div>
        );
      })}
    </div>
  );
}

export function PerformanceHeatmap({ monthly }: { monthly: MonthlyPerformance[] }) {
  const byMonth = new Map(monthly.map((month) => [month.month, month]));
  const years = [...new Set(monthly.map((month) => month.month.slice(0, 4)))].sort().reverse();
  if (!years.length) return <div className="mini-empty">月績效資料不足</div>;
  return (
    <div className="heatmap" aria-label="年度與月份績效熱圖">
      <div className="heatmap-head"><span>年度</span>{Array.from({ length: 12 }, (_, index) => <span key={index}>{index + 1}月</span>)}</div>
      {years.map((year) => (
        <div className="heatmap-row" key={year}>
          <strong>{year}</strong>
          {Array.from({ length: 12 }, (_, index) => {
            const month = byMonth.get(`${year}-${String(index + 1).padStart(2, "0")}`);
            const value = month?.returnPct ?? null;
            const intensity = value === null ? 0 : Math.min(1, Math.abs(value) / 8);
            const tone = value === null ? "na" : value === 0 ? "neutral" : value > 0 ? "positive" : "negative";
            const title = month
              ? [month.month, formatPercent(value, true), month.status, month.reason].filter(Boolean).join(" · ")
              : `${year}-${String(index + 1).padStart(2, "0")}: 無資料`;
            return (
              <span key={index} className={`${tone}${month?.status === "MTD" ? " mtd" : ""}${month?.status === "PARTIAL_INCEPTION" ? " partial" : ""}`}
                style={{
                  backgroundColor: value === null
                    ? undefined
                    : value === 0
                      ? undefined
                      : value > 0
                      ? `rgba(62, 231, 189, ${0.08 + intensity * 0.42})`
                      : `rgba(255, 113, 134, ${0.08 + intensity * 0.4})`,
                } as CSSProperties}
                title={title} tabIndex={0} aria-label={title}>
                {value === null ? "—" : value.toFixed(1)}
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export function UnderwaterChart({ points }: { points: DrawdownPoint[] }) {
  const sampled = downsample(points, 800);
  if (!sampled.length) return <div className="mini-empty">回撤資料不足</div>;
  const width = 1200;
  const height = 220;
  const from = sampled[0].recordedAt;
  const to = sampled.at(-1)!.recordedAt;
  const max = Math.max(1, ...sampled.map((point) => point.drawdownPct));
  const path = linePath(
    sampled.map((point) => ({ recordedAt: point.recordedAt, value: -point.drawdownPct })),
    width, height, -max, 0, from, to,
  );
  return (
    <div className="underwater-chart">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label="Underwater 回撤圖">
        <defs>
          <linearGradient id="drawdown-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffca72" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#ff6f86" stopOpacity="0.3" />
          </linearGradient>
        </defs>
        {[0, .5, 1].map((ratio) => <line key={ratio} x1="0" x2={width} y1={height * ratio} y2={height * ratio} className="chart-grid" />)}
        <path d={`${path} L${width},0 L0,0 Z`} fill="url(#drawdown-fill)" />
        <path d={path} className="underwater-line" />
      </svg>
      <div className="chart-dates"><span>{formatDate(from)}</span><span>−{max.toFixed(2)}%</span><span>{formatDate(to)}</span></div>
    </div>
  );
}
